import { defineStore, storeToRefs } from "pinia";
import { useToast } from "primevue/usetoast";
import { ref, watch } from "vue";

import { Role as IRole } from "@/composables/role-manager";
import { api } from "@/lib/openapi";
import { useCurrentEntityManager } from "@/stores/entity-store";
import { useReleaseStore } from "@/stores/release-store";
import { type SyncReturn, useSyncStore } from "@/stores/sync-store";

export type Role = IRole;

export const useRolesStore = defineStore("roles", () => {
    const roles = ref<Role[]>();
    const isLoading = ref(false);
    const toast = useToast();
    const syncStore = useSyncStore();
    const { domain, stek } = useCurrentEntityManager();
    const releaseStore = useReleaseStore();

    const controller = ref(new AbortController());

    const statusCode = ref<number>();
    const _error = ref();

    syncStore.$onAction(({ name, after }) => {
        if (name === "sync") {
            after((fullySyncedIdentities: SyncReturn) => {
                if (
                    fullySyncedIdentities.some(
                        (identity) =>
                            identity.attribute === "description" &&
                            identity.entityType === "role"
                    )
                ) {
                    if (stek.value) {
                        getRoles();
                    }
                }
            });
        }
    });
    const getRoles = async () => {
        controller.value.abort();
        controller.value = new AbortController();
        if (!domain.value?.identity) {
            roles.value = undefined;
            return;
        }
        isLoading.value = true;

        try {
            const { response, data } =
                await api.startDomainRoleMgtGetRolesStartPost({
                    body: {
                        domain_id: domain.value.identity,
                    },
                    signal: controller.value.signal,
                });

            statusCode.value = response.status;
            if (response.ok) {
                roles.value = data;
            } else {
                _error.value = data;
            }
        } catch (e) {
            if (e instanceof DOMException && e.name === "AbortError") {
                return;
            } else {
                throw e;
            }
        } finally {
            isLoading.value = false;
        }
    };

    watch(
        () => domain.value?.identity,
        async (newIdentity, oldIdentity) => {
            if (newIdentity && newIdentity !== oldIdentity) {
                isLoading.value = true;
                try {
                    await getRoles();
                } catch (e) {
                    if (e instanceof DOMException && e.name === "AbortError") {
                        // Request was aborted by the AbortController.
                        return;
                    } else {
                        throw e;
                    }
                } finally {
                    isLoading.value = false;
                }
            }
        },
        { immediate: true }
    );

    // Re-fetch on revision (thread branch / release tag) change — a ?thread=
    // query change doesn't alter route params, so the domain watcher won't fire.
    watch(
        () => releaseStore.activeRevision,
        async () => {
            await getRoles();
        }
    );

    watch(_error, () => {
        if (_error.value)
            toast.add({
                severity: "error",
                summary: "Error " + statusCode.value,
                detail: _error.value.detail
                    ? _error.value.detail[0].msg
                    : _error.value,
            });
        _error.value = undefined;
    });

    const updateRole = async (role: Role) => {
        if (!role) {
            return;
        }
        isLoading.value = true;

        const { response, data } =
            await api.startDomainRoleMgtUpdateRoleStartPost({
                body: {
                    role_id: role.identity,
                    name: role.name,
                },
            });

        statusCode.value = response.status;
        if (response.ok) {
            await getRoles();
        } else {
            _error.value = data;
        }
        isLoading.value = false;
    };

    const createRole = async (
        name: string,
        description: string
    ): Promise<Role | undefined> => {
        if (!name || !domain.value) {
            return;
        }
        isLoading.value = true;
        const { response, data } =
            await api.startDomainRoleMgtCreateRoleStartPost({
                body: {
                    domain_id: domain.value.identity,
                    name,
                    description: new File([description], name, {
                        type: "text/markdown",
                    }),
                },
            });

        statusCode.value = response.status;
        if (response.ok) {
            await getRoles();
            const createdRole = roles.value?.find((role) => role.name === name);
            isLoading.value = false;
            return createdRole;
        } else {
            _error.value = data;
        }
        isLoading.value = false;
    };

    const deleteRole = async (roleId: string) => {
        if (!roleId) {
            return;
        }

        isLoading.value = true;
        const { response, data } =
            await api.startDomainRoleMgtDeleteRoleStartPost({
                body: {
                    role_id: roleId,
                },
            });

        statusCode.value = response.status;
        if (response.ok) {
            await getRoles();
        } else {
            _error.value = data;
        }
        isLoading.value = false;
    };

    return {
        roles,
        isLoading,
        statusCode,
        getRoles,
        updateRole,
        createRole,
        deleteRole,
    };
});

export const useRolesManager = () => storeToRefs(useRolesStore());
